import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ArrowRight, PartyPopper, ShieldAlert, ChevronDown, ChevronUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLOR = {
  completed: "bg-emerald-500",
  in_progress: "bg-blue-500",
  blocked: "bg-red-500",
  not_started: "bg-stone-200",
};

const isRequired = (m) => (m?.name || "").includes("*");
const displayName = (name) => (name || "").replace(/\*/g, "").trim();

/**
 * MilestonesProgressStrip — compact clickable graphical summary of a
 * property's milestones (segmented progress bar + "what's next"), placed
 * below the property carousel. Clicking it expands/collapses the full
 * milestones panel inline on the page right below it (see
 * PropertyCardsGrid.jsx) — no modal.
 */
export function MilestonesProgressStrip({ propertyId, expanded = false, onClick }) {
  const [milestones, setMilestones] = useState([]);
  const [thresholdDays, setThresholdDays] = useState(3);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!propertyId) return;
    let alive = true;
    setLoading(true);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/property-milestones`, {
          params: { property_id: propertyId },
          withCredentials: true,
        });
        if (alive) {
          setMilestones(data?.milestones || []);
          setThresholdDays(data?.reminder_threshold_days || 3);
        }
      } catch {
        if (alive) setMilestones([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [propertyId]);

  if (!propertyId) return null;

  const total = milestones.length;
  const completed = milestones.filter((m) => m.status === "completed").length;
  const avgPercent = total
    ? Math.round(milestones.reduce((s, m) => s + (m.percent_complete || 0), 0) / total)
    : null;
  const next = milestones.find((m) => m.status !== "completed");
  const nextIsRequired = next && isRequired(next);

  const thresholdMs = thresholdDays * 24 * 60 * 60 * 1000;
  const overdueMilestones = milestones.filter((m) => {
    if (m.status !== "not_started" || !m.created_at) return false;
    return Date.now() - new Date(m.created_at).getTime() >= thresholdMs;
  });
  const hasReminder = overdueMilestones.length > 0;

  return (
    <button
      type="button"
      onClick={onClick}
      className="relative w-full text-left mt-6 p-4 border border-[#E5E5E0] bg-[#F9F9F6] hover:bg-[#0F291E]/5 transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-[#0F291E] focus-visible:outline-none"
      data-testid="milestones-progress-strip"
      aria-expanded={expanded}
    >
      <div className="flex items-center justify-between mb-2.5">
        <p className="text-xs uppercase tracking-[0.1em] text-stone-500 flex items-center gap-2">
          Home Dashboard
          {hasReminder && (
            <span
              className="relative inline-flex h-2 w-2"
              title={`${overdueMilestones.length} milestone(s) not started for ${thresholdDays}+ days: ${overdueMilestones.map((m) => displayName(m.name)).join(", ")}`}
              data-testid="milestones-reminder-dot"
            >
              <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
            </span>
          )}
        </p>
        <div className="flex items-center gap-2">
          {loading ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-stone-400" />
          ) : total > 0 ? (
            <span
              className="text-[10px] font-semibold px-1.5 py-0.5 border bg-[#E5E5E0] text-[#0F291E] border-[#E5E5E0]"
              data-testid="milestones-progress-percent"
            >
              {avgPercent}% &middot; {completed}/{total}
            </span>
          ) : null}
          {expanded ? (
            <ChevronUp className="h-3.5 w-3.5 text-stone-500" data-testid="milestones-strip-chevron-up" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 text-stone-500" data-testid="milestones-strip-chevron-down" />
          )}
        </div>
      </div>

      {!loading && total > 0 && (
        <div className="flex gap-0.5 mb-2.5" data-testid="milestones-progress-bar">
          {milestones.map((m) => {
            const blocking = isRequired(m) && m.status !== "completed";
            return (
              <span
                key={m.milestone_id}
                className={`h-1.5 flex-1 rounded-sm ${blocking ? "bg-red-500" : (STATUS_COLOR[m.status] || STATUS_COLOR.not_started)}`}
                title={`${displayName(m.name)} — ${blocking ? "required, not complete" : (m.status || "not_started").replace("_", " ")}`}
              />
            );
          })}
        </div>
      )}

      {!loading && (
        next ? (
          <p
            className={`flex items-center gap-1.5 text-sm ${nextIsRequired ? "text-red-600" : ""}`}
            data-testid="milestones-next-up"
          >
            {nextIsRequired ? (
              <ShieldAlert className="h-3.5 w-3.5 flex-shrink-0 text-red-500" />
            ) : (
              <ArrowRight className="h-3.5 w-3.5 flex-shrink-0 text-stone-500" />
            )}
            <span className={nextIsRequired ? "text-red-500" : "text-stone-500"}>
              {nextIsRequired ? "Required next:" : "Next up:"}
            </span>
            <span className={`font-medium truncate ${nextIsRequired ? "text-red-700" : "text-[#0F291E]"}`}>
              {displayName(next.name)}
            </span>
          </p>
        ) : total > 0 ? (
          <p className="flex items-center gap-1.5 text-sm text-emerald-700" data-testid="milestones-all-complete">
            <PartyPopper className="h-3.5 w-3.5 flex-shrink-0" />
            All milestones complete!
          </p>
        ) : (
          <p className="text-sm text-stone-500" data-testid="milestones-none-yet">
            No milestones yet — click to set up this property&apos;s checklist.
          </p>
        )
      )}
    </button>
  );
}

export default MilestonesProgressStrip;
